<template>
  <lf-card data-cy="bank-collection" class="mb-8 py-6">
    <template #header>
      <loader :is-loading="isLoading" :size="'25'" />
      <div class="flex w-full items-baseline justify-between">
        <lf-h3>{{ $t("DEALS.DEAL_PROGRESS.BANKING_COLLECTION.TITLE") }}</lf-h3>
      </div>
    </template>
    <template #default>
      <div class="row flex">
        <div class="border-r-2 border-gray-100 pr-6">
          <side-menu-item
            v-for="(name, item) in bankCollectionItems"
            :key="item"
            class="my-1-5"
            :active="activeItem === name"
            @click="setAsActive(name)"
          >
            {{ name }}
          </side-menu-item>
        </div>
        <div class="grow flex-col pl-8">
          <template v-if="activeItem === bankCollectionItems.plaidCollection">
            <div class="mt-3 mb-6 flex">
              <lf-chip
                :type="isPlaidConnected ? 'success' : 'failure'"
                uppercase
              >
                {{
                  $t("DEALS.DEAL_PROGRESS.BANKING_COLLECTION.PLAID_CONNECTION")
                }}
              </lf-chip>
              <lf-chip
                class="ml-4"
                :type="hasPlaidToken ? 'success' : 'failure'"
                uppercase
              >
                {{ $t("DEALS.DEAL_PROGRESS.BANKING_COLLECTION.PLAID_TOKEN") }}
              </lf-chip>
            </div>
            <div
              v-if="plaidStatus === PLAID_CREDENTIALS_CHANGED_MESSAGE"
              class="mt-3 mb-6 w-full flex-col rounded-sm bg-error/10"
            >
              <div class="flex items-center p-3">
                <icon-base :icon="IconFailBigRed" />
                <div class="pl-2">
                  {{ plaidStatus }}
                </div>
              </div>
            </div>
            <div v-if="canEditBankCollection" class="flex">
              <widget-link-field
                placeholder="Click the button to generate a new link"
                :link="widgetLink"
              />
              <span
                class="relative top-3 ml-6 cursor-pointer font-medium whitespace-nowrap text-primary"
                @click="generatePlaidLink"
              >
                {{
                  $t("DEALS.DEAL_PROGRESS.BANK_CONNECTION.GENERATE_PLAID_LINK")
                }}
              </span>
            </div>
          </template>
          <template v-if="activeItem === bankCollectionItems.bankAccDetails">
            <div class="mt-3 w-80 flex-col">
              <lf-input
                class="mb-3"
                placeholder="Bank Name"
                name="bank name"
                :value="bankAccount?.bank_name || '-'"
                readonly
              />
              <lf-input
                class="mb-3"
                placeholder="Account Number"
                name="account number"
                :value="bankAccount?.account_number || '-'"
                readonly
              />
              <lf-input
                placeholder="Account Type"
                name="account type"
                :value="bankAccount?.account_type || '-'"
                readonly
              />
            </div>
          </template>
        </div>
      </div>
    </template>
  </lf-card>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { PLAID_CREDENTIALS_CHANGED_MESSAGE } from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";

import WidgetLinkField from "./WidgetLinkField.vue";
import IconFailBigRed from "@/components/icons/IconFailBigRed.vue";
import Loader from "@/components/ui/Loader.vue";
import SideMenuItem from "@/components/ui/SideMenuItem.vue";

import type {
  ApplicationStatus,
  IBankAccount,
  ISummaryBankAccount,
} from "@/models/applications";
import type { PropType } from "vue";

const store = useStore();
const { t } = useI18n();

defineProps({
  appStatus: {
    type: Number as PropType<ApplicationStatus | null>,
    required: false,
  },
  appId: {
    type: String,
    default: "",
  },
  widgetToken: {
    type: String,
    default: "",
  },
  hasBankAccount: {
    type: Boolean,
    default: false,
  },
  isPlaidConnected: {
    type: Boolean,
    default: false,
  },
  hasPlaidToken: {
    type: Boolean,
    default: false,
  },
  bankAccount: {
    type: Object as PropType<ISummaryBankAccount | IBankAccount>,
  },
  plaidStatus: {
    type: String,
    default: "",
  },
});

const { isFunder } = useAuth();

const { canEditDealApplicationAndUnderwriting, canEditDealProgress } =
  useDeals();

const canEditBankCollection = computed(
  () =>
    !isFunder &&
    (canEditDealApplicationAndUnderwriting.value || canEditDealProgress.value)
);

const bankCollectionItems = {
  plaidCollection: t("DEALS.DEAL_PROGRESS.BANKING_COLLECTION.PLAID_COLLECTION"),
  bankAccDetails: t("DEALS.DEAL_PROGRESS.BANKING_COLLECTION.BANK_ACC_DETAILS"),
};

const isLoading = ref(false);
const activeItem = ref(bankCollectionItems.plaidCollection);
const widgetLink = ref("");

const setAsActive = (item: string) => (activeItem.value = item);

const generatePlaidLink = async () => {
  const link = await store.dispatch("applications/generatePlaidLink");
  widgetLink.value = link;
};
</script>
